import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../../utils/axiosConfig";
import "./AccessoryParameters.css";

const INPUT_TYPES = ["Text", "Number", "Dropdown"];
const defaultForm = { name: "", input_type: "Text" };

function AccessoryParameters() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [accessory, setAccessory] = useState(null);
  const [parameters, setParameters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editParam, setEditParam] = useState(null);
  const [paramToDelete, setParamToDelete] = useState(null);
  const [formData, setFormData] = useState(defaultForm);
  const [message, setMessage] = useState({ type: "", text: "" });

  // Dropdown values management
  const [showValuesModal, setShowValuesModal] = useState(false);
  const [selectedParam, setSelectedParam] = useState(null);
  const [paramValues, setParamValues] = useState([]);
  const [newValueText, setNewValueText] = useState("");
  const [loadingValues, setLoadingValues] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [accRes, paramsRes] = await Promise.all([
        axios.get(`/accessories/${id}`),
        axios.get(`/accessories/${id}/parameters`),
      ]);
      setAccessory(accRes.data.data);
      setIsLocked(accRes.data.data?.is_locked || false);
      setParameters(paramsRes.data.data || []);
    } catch (error) {
      showMessage("error", error.response?.data?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: "", text: "" }), 5000);
  };

  const openAddModal = () => {
    setEditParam(null);
    setFormData(defaultForm);
    setShowModal(true);
  };

  const openEditModal = (param) => {
    setEditParam(param);
    setFormData({ name: param.name, input_type: param.input_type });
    setShowModal(true);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editParam) {
        await axios.put(`/accessories/${id}/parameters/${editParam.id}`, {
          name: formData.name,
          input_type: formData.input_type,
        });
        showMessage("success", "Parameter updated successfully");
      } else {
        await axios.post(`/accessories/${id}/parameters`, {
          name: formData.name,
          input_type: formData.input_type,
        });
        showMessage("success", "Parameter added successfully");
      }
      setShowModal(false);
      fetchData();
    } catch (error) {
      showMessage("error", error.response?.data?.message || "Error occurred");
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/accessories/${id}/parameters/${paramToDelete.id}`);
      showMessage("success", "Parameter deleted successfully");
      setParamToDelete(null);
      fetchData();
    } catch (error) {
      showMessage("error", error.response?.data?.message || "Failed to delete");
    }
  };

  // Dropdown values management functions
  const openValuesModal = async (param) => {
    setSelectedParam(param);
    setShowValuesModal(true);
    setLoadingValues(true);
    try {
      const res = await axios.get(`/accessories/parameters/${param.id}/values`);
      setParamValues(res.data.data || []);
    } catch (error) {
      showMessage("error", "Failed to load dropdown values");
      setParamValues([]);
    } finally {
      setLoadingValues(false);
    }
  };

  const closeValuesModal = () => {
    setShowValuesModal(false);
    setSelectedParam(null);
    setParamValues([]);
    setNewValueText("");
  };

  const handleAddValue = async () => {
    if (!newValueText.trim() || !selectedParam) return;
    try {
      await axios.post(`/accessories/parameters/${selectedParam.id}/values`, {
        value_text: newValueText.trim(),
      });
      // Refresh values
      const res = await axios.get(`/accessories/parameters/${selectedParam.id}/values`);
      setParamValues(res.data.data || []);
      setNewValueText("");
      showMessage("success", "Value added successfully");
    } catch (error) {
      showMessage("error", error.response?.data?.message || "Failed to add value");
    }
  };

  const handleDeleteValue = async (valueId) => {
    try {
      await axios.delete(`/accessories/parameters/values/${valueId}`);
      // Refresh values
      const res = await axios.get(`/accessories/parameters/${selectedParam.id}/values`);
      setParamValues(res.data.data || []);
      showMessage("success", "Value deleted successfully");
    } catch (error) {
      showMessage("error", error.response?.data?.message || "Failed to delete value");
    }
  };

  if (loading) {
    return (
      <div className="accessory-params-container">
        <div className="loading-container">Loading...</div>
      </div>
    );
  }

  return (
    <div className="accessory-params-container">
      <div className="accessory-params-header">
        <div>
          <button
            className="btn-back"
            onClick={() => navigate("/admin/accessories-config")}
          >
            ← Back to Accessories
          </button>
          <h1 className="accessory-params-title">{accessory?.name} — Parameters</h1>
          <p className="accessory-params-subtitle">
            Define parameter fields shown in the Accessory Component Master form
          </p>
        </div>
        <button
          className="btn-add-param"
          onClick={openAddModal}
          disabled={isLocked}
          title={isLocked ? "Accessory is locked. Cannot add parameters." : "Add new parameter"}
          style={{ opacity: isLocked ? 0.5 : 1, cursor: isLocked ? 'not-allowed' : 'pointer' }}
        >
          + Add Parameter
        </button>
        {isLocked && <div className="lock-warning">🔒 This accessory is locked</div>}
      </div>

      {message.text && (
        <div className={`alert-message alert-${message.type}`}>{message.text}</div>
      )}

      <div className="table-card">
        <div className="table-header-fixed">
          <table className="params-table">
            <thead>
              <tr>
                <th>Parameter Name</th>
                <th>Input Type</th>
                <th>Actions</th>
              </tr>
            </thead>
          </table>
        </div>
        <div className="table-body-scroll">
          <table className="params-table">
            <tbody>
              {parameters.length === 0 ? (
                <tr>
                  <td colSpan="3" className="no-data-row">
                    No parameters defined. Click "+ Add Parameter" to add one.
                  </td>
                </tr>
              ) : (
                parameters.map((param) => (
                  <tr key={param.id}>
                    <td className="param-name">{param.name}</td>
                    <td>
                      <span className={`type-badge type-${param.input_type.toLowerCase()}`}>
                        {param.input_type}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        {/* Manage Values button - only for Dropdown parameters */}
                        {param.input_type === "Dropdown" && (
                          <button
                            className="btn-values"
                            onClick={() => openValuesModal(param)}
                            title="Manage dropdown values"
                          >
                            Values
                          </button>
                        )}
                        <button
                          className="btn-edit"
                          onClick={() => openEditModal(param)}
                        >
                          Edit
                        </button>
                        <button
                          className="btn-delete"
                          onClick={() => setParamToDelete(param)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              {editParam ? "Edit Parameter" : "Add Parameter"}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">
                  Parameter Name <span className="required-mark">*</span>
                </label>
                <input
                  className="form-input"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g. Manufacturer"
                  required
                  maxLength={150}
                  autoFocus
                />
                <span className="help-text">
                  Must be unique within this accessory (case-insensitive)
                </span>
              </div>
              <div className="form-group">
                <label className="form-label">
                  Input Type <span className="required-mark">*</span>
                </label>
                <div className="radio-group">
                  {INPUT_TYPES.map((type) => (
                    <label key={type} className="radio-label">
                      <input
                        type="radio"
                        name="input_type"
                        value={type}
                        checked={formData.input_type === type}
                        onChange={handleInputChange}
                      />
                      <span>{type}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="modal-actions">
                <button
                  className="btn-cancel"
                  type="button"
                  onClick={() => setShowModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="btn-submit"
                  type="submit"
                  disabled={isLocked && !editParam}
                >
                  {editParam ? "Update" : "Add"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {paramToDelete && (
        <div className="modal-overlay" onClick={() => setParamToDelete(null)}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <h2 className="confirm-title">Confirm Deletion</h2>
            <p className="confirm-text">
              Delete parameter "<strong>{paramToDelete.name}</strong>"?
            </p>
            <div className="confirm-warning">
              If this parameter is used in existing component configurations, deletion will be blocked.
            </div>
            <div className="confirm-actions">
              <button className="btn-cancel" onClick={() => setParamToDelete(null)}>
                Cancel
              </button>
              <button className="btn-confirm-delete" onClick={handleDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Manage Dropdown Values Modal */}
      {showValuesModal && selectedParam && (
        <div className="modal-overlay" onClick={closeValuesModal}>
          <div className="modal-content values-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">Manage Values: {selectedParam.name}</h2>
            
            {/* Add new value */}
            <div className="add-value-section">
              <input
                className="form-input"
                type="text"
                value={newValueText}
                onChange={(e) => setNewValueText(e.target.value)}
                placeholder="Enter new value"
                onKeyDown={(e) => e.key === "Enter" && handleAddValue()}
              />
              <button 
                className="btn-add-value"
                onClick={handleAddValue}
                disabled={!newValueText.trim()}
              >
                Add
              </button>
            </div>

            {/* Values list */}
            <div className="values-list">
              {loadingValues ? (
                <div className="loading-values">Loading values...</div>
              ) : paramValues.length === 0 ? (
                <div className="no-values">No values defined yet. Add one above.</div>
              ) : (
                paramValues.map((val) => (
                  <div key={val.id} className="value-item">
                    <span className="value-text">{val.value_text}</span>
                    <button
                      className="btn-delete-value"
                      onClick={() => handleDeleteValue(val.id)}
                      title="Delete value"
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="modal-actions">
              <button className="btn-cancel" onClick={closeValuesModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccessoryParameters;
